'use client';

function SkeletonBlock({ className }: { className: string }) {
  return <div className={`rounded bg-gray-200 ${className}`} />;
}

function MobilePlanCardSkeleton() {
  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-8 text-center shadow-lg">
      <SkeletonBlock className="mx-auto mb-6 h-16 w-16 rounded-full" />
      <SkeletonBlock className="mx-auto mb-4 h-7 w-40" />
      <SkeletonBlock className="mx-auto mb-4 h-8 w-28 rounded-full" />
      <SkeletonBlock className="mx-auto mb-2 h-10 w-32" />
      <SkeletonBlock className="mx-auto mb-6 h-4 w-24" />
      <div className="mb-8 space-y-3 text-left">
        <SkeletonBlock className="h-5 w-36" />
        {Array.from({ length: 5 }).map((_, index) => (
          <div key={index} className="flex items-center gap-3">
            <SkeletonBlock className="h-5 w-5 rounded-full" />
            <SkeletonBlock className="h-4 flex-1" />
          </div>
        ))}
      </div>
      <SkeletonBlock className="h-12 w-full rounded-xl" />
    </div>
  );
}

function DesktopComparisonSkeleton() {
  const planColumns = 3;

  return (
    <div className="hidden w-full animate-pulse md:block">
      <div className="w-full overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-lg">
        <div className="border-b border-gray-200 bg-gradient-to-b from-gray-50 to-white px-4 py-5">
          <div
            className="grid items-stretch gap-3"
            style={{ gridTemplateColumns: `220px repeat(${planColumns}, minmax(156px, 1fr))` }}
          >
            <div className="rounded-lg border border-gray-200 bg-white px-3 py-4">
              <SkeletonBlock className="mx-auto mb-2 h-10 w-10 rounded-full" />
              <SkeletonBlock className="mx-auto h-4 w-20" />
              <SkeletonBlock className="mx-auto mt-2 h-3 w-24" />
            </div>
            {Array.from({ length: planColumns }).map((_, index) => (
              <div key={index} className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-4">
                <SkeletonBlock className="mx-auto mb-2 h-10 w-10 rounded-full" />
                <SkeletonBlock className="mx-auto mb-2 h-4 w-20" />
                <SkeletonBlock className="mx-auto mb-2 h-5 w-16 rounded-full" />
                <SkeletonBlock className="mx-auto mb-2 h-8 w-24" />
                <SkeletonBlock className="mx-auto h-3 w-20" />
                <SkeletonBlock className="mx-auto mt-3 h-9 w-full rounded-lg" />
              </div>
            ))}
          </div>
        </div>

        <div className="space-y-0 px-4 py-2">
          {Array.from({ length: 8 }).map((_, index) => (
            <div
              key={index}
              className="grid items-center gap-3 border-b border-gray-100 py-3 last:border-b-0"
              style={{ gridTemplateColumns: `220px repeat(${planColumns}, minmax(156px, 1fr))` }}
            >
              <SkeletonBlock className="h-4 w-40" />
              {Array.from({ length: planColumns }).map((__, cellIndex) => (
                <div key={cellIndex} className="flex justify-center">
                  <SkeletonBlock className="h-5 w-5 rounded-full" />
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export function MembershipPricingSkeleton() {
  return (
    <div className="animate-pulse">
      <DesktopComparisonSkeleton />
      <div className="mx-auto max-w-6xl space-y-8 md:hidden">
        <MobilePlanCardSkeleton />
        <MobilePlanCardSkeleton />
        <MobilePlanCardSkeleton />
      </div>
    </div>
  );
}

export function MembershipFeatureGridSkeleton() {
  return (
    <div className="mx-auto grid max-w-6xl animate-pulse grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
      {Array.from({ length: 6 }).map((_, index) => (
        <div key={index} className="p-6 text-center">
          <SkeletonBlock className="mx-auto mb-4 h-20 w-20 rounded-full" />
          <SkeletonBlock className="mx-auto mb-3 h-6 w-36" />
          <SkeletonBlock className="mx-auto mb-2 h-4 w-full max-w-xs" />
          <SkeletonBlock className="mx-auto h-4 w-4/5 max-w-sm" />
        </div>
      ))}
    </div>
  );
}

export function MembershipSupportSkeleton() {
  return (
    <div className="mx-auto grid max-w-4xl animate-pulse grid-cols-1 gap-8 md:grid-cols-2">
      {Array.from({ length: 2 }).map((_, index) => (
        <div key={index} className="rounded-2xl bg-white p-8 shadow-lg max-[599px]:p-6">
          <SkeletonBlock className="mx-auto mb-6 h-20 w-20 rounded-full" />
          <SkeletonBlock className="mx-auto mb-4 h-7 w-48" />
          <SkeletonBlock className="mx-auto mb-6 h-4 w-full max-w-sm" />
          <div className="mb-6 space-y-3">
            {Array.from({ length: 3 }).map((__, rowIndex) => (
              <div key={rowIndex} className="flex items-center gap-3">
                <SkeletonBlock className="h-5 w-5 rounded-full" />
                <SkeletonBlock className="h-4 flex-1" />
              </div>
            ))}
          </div>
          <SkeletonBlock className="h-12 w-full rounded-xl" />
        </div>
      ))}
    </div>
  );
}

export function MembershipFaqSkeleton() {
  return (
    <div className="mx-auto max-w-4xl animate-pulse space-y-4">
      {Array.from({ length: 5 }).map((_, index) => (
        <div key={index} className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
          <div className="flex items-center justify-between px-6 py-4 max-[599px]:px-4">
            <SkeletonBlock className="h-5 w-4/5 max-w-lg" />
            <SkeletonBlock className="h-5 w-5 rounded" />
          </div>
        </div>
      ))}
    </div>
  );
}
